/**
 * PostgREST errors carry a code and a raw database message. Turn the ones a user can act on
 * into plain sentences, and pass a deliberately raised exception through as written - every
 * `raise exception` in the migrations is already phrased for a person.
 */
export function describeError(error: { message?: string; code?: string } | null): string {
  if (!error) return '';
  const message = error.message ?? 'Something went wrong.';
  if (error.code === '42501' || /permission denied|insufficient_privilege/i.test(message)) {
    return 'Your role does not allow that. Ask your organisation admin if you need it.';
  }
  // Only Postgres's OWN constraint messages are replaced. A `raise exception ... using errcode =
  // 'check_violation'` carries the same code with a sentence written for a person ("More trees than
  // permit TP/1 allows ..."), and replacing that hid the one thing that said what to fix.
  if (error.code === '23505' && /duplicate key value/i.test(message)) return 'That value is already used on another record.';
  if (error.code === '23503' && /violates foreign key constraint/i.test(message)) return 'A linked record is missing or was removed.';
  if (error.code === '23514' && /violates check constraint/i.test(message)) {
    // A column's own "not in the future" check is named <table>_<date column>_check (issued_on,
    // dispatched_on, taken_on ...): say which rule it was rather than a riddle.
    const constraint = /check constraint "([^"]+)"/i.exec(message)?.[1] ?? '';
    if (/_(on|date)_check$/.test(constraint)) return 'That date cannot be in the future.';
    return 'That combination of values is not allowed on this record.';
  }
  return message.replace(/^.*?(?:ERROR|error):\s*/i, '');
}
